import { Category, CodeType, Entity, SupervisorCheck, UoM, CodeUoM, TaxRate, PriceListCategory, CustomFieldValue, InvoiceStatus, EntitySubStatus } from '../index';
import { Source } from '../../enums/enum.source';
import { ItemLinkEntity } from '../entity';
import { EntityType, PagingOptions, SystemLabels } from '.';
import { WorkOrderStatus } from '../job';
import { ContactEntity } from '../contact';

export enum CostSource {
    StandardCost = 'Standard Cost',
    TradePrice = 'Trade',
    // Manual = 'Manual'
}

export enum CodeStatus {
    Normal = 'Normal',
    Variation = 'Variation',
    Cancelled = 'Cancelled',

    /** @deprecated  Use 'Requested' instead */
    Pending = 'Pending',

    Requested = 'Requested',
    PendingReview = 'Pending Review',
    Declined = 'Declined',

    Deleted = 'Deleted',
}

export enum CodeInvoiceStatus {
    Billable = 'Billable',
    NoCharge = 'No Charge',
    NotBillable = 'Not Billable'
}

export interface GroupCodeCategory {
    categoryName: string;
    subcategories: Category[];
}

export interface CodeCategory {
    _id?: string;
    name: string;
    categoryId: string;
    categoryName: string;
    subCategoryId: string;
    subCategoryName: string;
}


export const recentCodeCategory = {
    _id: 'recent',
    name: 'System | Recently Added',
    categoryName: '<System>',
    categoryId: 'systemRecent',
    subCategoryName: 'Recently Added',
    subCategoryId: 'recent',
} as CodeCategory;

export const zeroPriceCodeCategory = {
    _id: 'zero',
    name: 'System | Items with Zero Price',
    categoryName: '<System>',
    categoryId: 'systemZero',
    subCategoryName: 'Items with Zero Price',
    subCategoryId: 'zero',
} as CodeCategory;

export const searchCodeCategory = {
    _id: 'search',
    name: 'System | Search',
    categoryName: '<System>',
    categoryId: 'systemSearch',
    subCategoryName: 'Search',
    subCategoryId: 'search',
} as CodeCategory;

export interface CodePriceBook {
    _id: string;
    name: string;
    category?: PriceListCategory;
    price: number;
    codeId?: string;

    updatedAt?: Date;
    // updatedBy?: Entity;
}


export interface JobCodeExport {
    workOrderNumber?: string;
    name: string;
    location: string;
    quantity: number;
    note: string;
    buyPrice: number;
    sellPrice: number;
    status: CodeStatus;
    codeUoM: CodeUoM;
}

export interface Code extends Entity {
    type: CodeType;
    tenantName?: string;
    parentType?: CodeType;
    category: CodeCategory;
    description: string;
    longDescription?: string;
    jobNumber?: string;
    childCodes?: ChildCode[];
    uoms?: CodeUoM[];
    location?: string;
    quantity?: number;
    customFields?: any;
    suppliedBySubcontractor?: boolean;
    // priceBooks?: CodePriceBook[];

    autoCreateTask?: boolean;
    isClaimCode?: boolean;
}
export interface CatalogueCode extends Code {
    //jobNumber?: string;
    childCodes?: ChildCode[];
    //locked?: boolean;
    labels?: number;
    uoms?: CodeUoM[]; // Additional uom list
    baseUoM?: UoM;
    manufacturer?: string;
    retailPrice?: number;
    tradePrice?: number;
    standardCost?: number;
    economicOrderQty?: number;
    safetyStockQty?: number;
    leadTimeDays?: number;
    longDescription?: string;
    upc?: string;

    itemSold?: boolean;
    saleGlCode?: string;
    saleTaxRate?: TaxRate;
    codeInvoicingStatus?: CodeInvoiceStatus;

    itemPurchased?: boolean;
    purchaseGlCode?: string;
    purchaseTaxRate?: TaxRate;

    canOverrideDesc?: boolean;

    trackingCategory1?: string;
    trackingCategory2?: string;

}

export interface CodeFinance {
    _id: string;
    name: string;

    itemSold?: boolean;
    saleGlCode?: string;
    saleTaxRate?: TaxRate;

    itemPurchased?: boolean;
    purchaseGlCode?: string;
    purchaseTaxRate?: TaxRate;
}

export function ToEmbeddedCode(c: CatalogueCode): EmbeddedCodeLike {

    const item = {
        type: c.type,
        parentType: c.parentType,
        category: c.category,
        description: c.description,
        longDescription: c.longDescription,
        // priceBooks: c.priceBooks,
        quantity: c.quantity ?? 1,
        location: c.location ?? '',
        customFields: c.customFields,
        name: c?.name?.trim(),
        _id: c._id,
        suppliedBySubcontractor: c.suppliedBySubcontractor,
        codeUoM: c?.['codeUoM'] ?? { uom: c.baseUoM, conversionRate: 1 },

        manufacturer: c.manufacturer,

        itemSold: c.itemSold,
        saleGlCode: c.saleGlCode,
        saleTaxRate: c.saleTaxRate,

        itemPurchased: c.itemPurchased,
        purchaseGlCode: c.purchaseGlCode,
        purchaseTaxRate: c.purchaseTaxRate,

        autoCreateTask: c.autoCreateTask,
        canOverrideDesc: c.canOverrideDesc,
        data: c.data
    } as EmbeddedCode;

    if (item.parentType === 'BUNDLE') {
        item.codeInvoicingStatus = c.codeInvoicingStatus ?? CodeInvoiceStatus.Billable;
    }
    else {
        item.codeInvoicingStatus = c?.itemSold
            ? c?.codeInvoicingStatus ?? CodeInvoiceStatus.Billable
            : c?.codeInvoicingStatus ?? CodeInvoiceStatus.Billable
    }

    return item;
}

export type AdjustmentType = 'Value' | 'Percent';

export interface EmbeddedCode extends Code {
    source?: Source;
    buyPrice?: number;
    sellPrice?: number;
    customBuyPrice?: boolean;
    customSellPrice?: boolean;
    completed?: boolean;
    answered?: boolean;
    supervisorCheck?: SupervisorCheck;
    status?: CodeStatus;
    integrationId?: string;
    location?: string;
    note?: string;
    codeUoM?: CodeUoM;
    baseQuantity?: number;
    codeInvoicingStatus?: CodeInvoiceStatus;
    suppliedBySubcontractor?: boolean;
    customFields?: any;
    upc?: string;
    uom?: string;
    bill?: ItemLinkEntity;
    invoice?: ItemLinkEntity;
    purchaseOrder?: ItemLinkEntity;
    stage?: ItemLinkEntity;

    itemSold?: boolean;
    saleGlCode?: string;
    saleTaxRate?: TaxRate;

    itemPurchased?: boolean;
    purchaseGlCode?: string;
    purchaseTaxRate?: TaxRate;
    defectCount?: number;
    timesheetIds?: string[];

    manufacturer?: string;

    /** Id Code from Setting > Items  */
    catalogueCodeId?: string;
    sortOrder?: number;

    canOverrideDesc?: boolean;
    adjustmentType?: AdjustmentType;
    adjustment?: number;
    adjustedSell?: number;

    totalBuy?: number;
    totalSellEx?: number;
    totalAdjustment?: number;
    totalTax?: number;
    totalSellInc?: number;
    itemTaskCompleted?: boolean;

    auditDetails?: CodeAuditDetails;
    claimLinked?: boolean;
    claimImport?: boolean;
    isReadonly?: boolean;
}

export interface PendingCode extends Omit<EmbeddedCode, 'status'> {
    itemStatus?: EntitySubStatus;
}

export interface ApprovedCode extends EmbeddedCode {
}

export type EmbeddedCodeLike = EmbeddedCode & PendingCode & ApprovedCode;

export interface ChildCode extends Entity {
    description?: string;
    category?: CodeCategory;
    quantity?: number;
    codeUoM?: CodeUoM;
    codeInvoicingStatus?: CodeInvoiceStatus;
    suppliedBySubcontractor?: boolean;
}

export interface CodeImportSystemResponse {
    name: string;
    message: string[];
    index: number;
    expanded?: boolean;
}

export interface PatchJobCodeRequest {
    jobId?: string;
    workOrderId: string;
    codeId: string;
    defectCount?: number;
}

export interface PatchItemsRequest {
    jobId?: string;
    workOrderItemPatchData: PatchWorkOrderItemsRequest[];
}

export interface BulkActionItemsRequest extends PatchItemsRequest {
    action: WorkOrderCodesBulkAction;
}

export interface BulkActionItemsResponse {
    action: WorkOrderCodesBulkAction;
    responseData: any
}

export interface PatchWorkOrderItemsRequest {
    workOrderId: string;
    codes: EmbeddedCodeLike[];
}

export interface WorkOrderCodeEntity extends Entity {
    workOrderNumber: string;
    technician: Entity;
    resources: ContactEntity[];
    status: WorkOrderStatus;
    jobSystemLabels: SystemLabels[];
    workOrderName?: string;
    workCategory?: Entity;
    sortOrder?: string;
}
export interface EmbeddedCodeGroup extends EmbeddedCodeLike {
    job: Entity;
    workOrder: WorkOrderCodeEntity;
    location: string;
    entityType?: EntityType;
    selected?: boolean;

    canEdit?: boolean;
    canDelete?: boolean;
    canEditFields?: Partial<Record<keyof EmbeddedCode, boolean>>;

    billApproved?: boolean;
    billStatus?: InvoiceStatus;

    invoiceApproved?: boolean;
    invoiceStatus?: InvoiceStatus;
}


export interface EmbeddedCodeMetadataTab {
    job: Entity;
    workOrder: WorkOrderCodeEntity;
    dateFormat: string;
    timezone: string;
    type: EntityType;
    workOrders?: WorkOrderCodeEntity[];
    sellPricebook?: Entity;
    workOrderUpdatedAt?: Date;
    currentUser?: Entity;
    woReadOnlyAfterFinalised: boolean;
    jobSystemLabels?: Entity[];
}

export interface CodeAndCustomFieldResponse {
    customFields: CustomFieldValue[];
    codeSummary: Partial<CatalogueCode>;
}

export interface KitItemEntity extends Entity {
    category: CodeCategory;
}

export interface CodeCategoryEntity extends Entity {
    description: string;
    type: CodeType;
}

export interface GetCodeCategoryEntitiesRequest {
    category?: string;
    excludePricebook?: string;
    isCodeItem?: boolean;
    isSold?: boolean;
    isPurchased?: boolean;
    excludeTypes: CodeType[];
    paging?: PagingOptions;

}

export interface CodeAuditDetails {
    completedBy?: ContactEntity;
    completedAt?: Date;
    actualQuantity?: number;
    comply?: CodeAuditComply;
    complyNote?: string;
}

export enum CodeAuditComply {
    Pass = 'Pass',
    Fail = 'Fail',
    NA = 'NA'
}

export enum EmbeddedCodeGroupBy {
    WorkOrders = 'Stages',
    Location = 'Location',
    Status = 'Status',
    None = 'None'
}

export enum EmbeddedCodeFilterDefinition {
    AllActive = 'All Active',
    AllInactive = 'All Inactive',
    AllPending = 'All Pending',
    Normal = 'Normal',
    Variation = 'Variation',
    Cancelled = 'Cancelled',
    AllPendingActive = 'All Pending Active',
    AllPendingInactive = 'All Pending Inactive',
}

export enum EmbeddedCodeStateFilterDefinition {
    All = "All",
    Complete = "Complete",
    Incomplete = "Incomplete",
    SuppliedByContactor = "Supplied By Contractor",
    NotSuppliedByContractor = "Not Supplied By Contractor",
    CanBePurchased = "Items to Purchase",
    ItemsOrdered = "Items Ordered",
}

export enum WorkOrderCodesBulkAction {
    Approved = 'Approved',
    UpdateStatus = 'Update Status',
    Delete = 'Delete',
    Cancel = 'Cancel',
}

export interface EmbeddedCodeViewOptions {
    groupBy: EmbeddedCodeGroupBy;
    statusFilters: EmbeddedCodeFilterDefinition[];
    stateFilter: EmbeddedCodeStateFilterDefinition;
    search?: string,

    isTextSearch?: boolean;
}